// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { Document } from '@shared/data-access/types/engine-types';
import dayjs from 'dayjs';
import type { DocumentFiltersState } from '../types/document-filters';

const isDateInRange = (
  date: string | null | undefined,
  from: string,
  to: string,
) => {
  if (!from && !to) {
    return true;
  }

  if (!date) {
    return false;
  }

  const value = dayjs(date.split('T')[0]).startOf('day');

  if (!value.isValid()) {
    return false;
  }

  if (from && value.isBefore(dayjs(from).startOf('day'))) {
    return false;
  }

  return !(to && value.isAfter(dayjs(to).startOf('day')));
};

export const filterDocuments = (
  documents: readonly Document[],
  filters: DocumentFiltersState,
): Document[] => {
  const query = filters.search.trim().toLowerCase();

  return documents.filter(document => {
    if (filters.scope !== 'all' && document.scope !== filters.scope) {
      return false;
    }

    if (filters.category && document.category !== filters.category) {
      return false;
    }

    if (filters.subcategory && document.subcategory !== filters.subcategory) {
      return false;
    }

    if (filters.language && document.language !== filters.language) {
      return false;
    }

    if (
      !isDateInRange(
        document.document_date,
        filters.document_date_from,
        filters.document_date_to,
      )
    ) {
      return false;
    }

    if (
      !isDateInRange(
        document.expiry_date,
        filters.expiry_date_from,
        filters.expiry_date_to,
      )
    ) {
      return false;
    }

    if (!query) {
      return true;
    }

    return [
      document.title,
      document.issuer,
      document.description,
      document.category,
      document.subcategory,
      document.language,
      ...(document.files?.map(file => file.file_name) ?? []),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(query);
  });
};
